<template>
  <div class="objectives">
    <div class="objectives__container">
      <div class="objectives__header-wrapper">
        <div class="objectives__header-title">Objectives</div>
        <div class="objectives__header-completed">
          <div class="objectives__header-completed-label">
            Completed objectives:
          </div>
          <div
            :class="[
              'objectives__header-completed-badge',
              allObjectivesCompleted &&
                'objectives__header-completed-badge--all',
            ]"
          >
            {{ objectivesCompleted.length }}/{{ labObjectives.length }}
          </div>
        </div>
      </div>
      <div class="objectives__description">
        <p>To finish the lab, complete all objectives.</p>
      </div>
      <ul class="objectives__list">
        <li
          class="objectives__list-item"
          v-for="objective in labObjectives"
          :key="objective.id"
        >
          <div class="objectives__list-checkbox-wrapper">
            <label
              :class="[
                'answer-checkbox',
                'objectives__list-checkbox',
                (!labRunning ||
                  objectiveChecked(objective.id) ||
                  isSubmitting) &&
                  'objectives__list-checkbox--disabled',
              ]"
            >
              <span
                class="answer-checkbox__input objectives__list-checkbox__input"
              >
                <input
                  type="checkbox"
                  id="objetive.id"
                  :disabled="
                    !labRunning ||
                      objectiveChecked(objective.id) ||
                      isSubmitting
                  "
                  :name="`${objective.id}-checkbox`"
                  :value="objective.id"
                  v-model="currentSelection"
                />
                <span
                  :class="[
                    'answer-checkbox__control',
                    'objectives__list-checkbox__control',
                    (!labRunning || isSubmitting) &&
                      'objectives__list-checkbox__control--disabled',
                    objectiveChecked(objective.id) &&
                      'objectives__list-checkbox__control--completed',
                  ]"
                >
                  <CheckboxTick />
                </span>
              </span>
            </label>
          </div>
          <div class="objectives__list-description-wrapper">
            <div
              class="objectives__list-description"
              v-html="objective.description_html"
            ></div>
            <div
              class="objectives__list-submission"
              v-if="showSubmissionLabel(objective)"
            >
              <div
                :class="[
                  objective.has_passed
                    ? 'objectives__list-submission--passed'
                    : 'objectives__list-submission--fail',
                ]"
              >
                <GreencheckIcon v-if="lastSubmission(objective).has_passed" />
                <FailIcon v-else />
              </div>
              <div class="objectives__list-submission-wrapper">
                <p class="objectives__list-submission-reason">
                  <span>{{ lastSubmission(objective).label }} </span
                  >{{ lastSubmission(objective).reason }}
                </p>
                <div
                  class="objectives__list-submission-reset"
                  @click="resetObjective(objective.id)"
                  v-if="objectiveHasPassed(objective.id)"
                >
                  Reset
                </div>
                <div
                  class="objectives__list-submission-info"
                  @mouseleave="updateTooltip(objective.id, false)"
                  @mouseover="updateTooltip(objective.id, true)"
                  v-if="objectiveHasPassed(objective.id)"
                >
                  <InfoIcon />
                </div>
                <div
                  class="objectives__list-submission-tooltip"
                  v-if="objectiveTooltipId === objective.id"
                >
                  Resetting an objective will allow you to practice more without
                  affecting your completion status.
                </div>
              </div>
            </div>
          </div>
          <div
            :class="[
              'objectives__list-btn',
              objective.has_passed && 'objectives__list-btn--completed',
            ]"
          >
            {{ objective.has_passed ? 'COMPLETE' : 'INCOMPLETE' }}
          </div>
        </li>
      </ul>
    </div>
    <div class="objectives__footer">
      <div class="objectives__footer-status">
        <div
          class="objectives__footer-submitting"
          :style="{ visibility: isSubmitting ? 'visible' : 'hidden' }"
        >
          <LoadingSmallSpinner />Submitting
        </div>
        <div class="objectives__footer-checked ">
          {{ objectivesStatusLabel }}
        </div>
      </div>
      <div class="objectives__footer-submit">
        <button
          class="btn btn--primary"
          :disabled="!labRunning || !currentSelection.length || isSubmitting"
          @click="submitPractice"
        >
          Submit objectives
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import CheckboxTick from '@/assets/images/certification/checkbox-tick.svg'
import FailIcon from '@/assets/icons/fail_icon.svg'
import GreencheckIcon from '@/assets/icons/greencheck.svg'
import LoadingSmallSpinner from '@/components/loaders/LoadingSmallSpinner.vue'
import { InfoIcon } from 'vue-feather-icons'

export default {
  name: 'LabObjectives',
  components: {
    CheckboxTick: { ...CheckboxTick, name: 'CheckboxTick' },
    FailIcon: { ...FailIcon, name: 'FailIcon' },
    GreencheckIcon: { ...GreencheckIcon, name: 'GreencheckIcon' },
    InfoIcon: { ...InfoIcon, name: 'InfoIcon' },
    LoadingSmallSpinner,
  },
  data() {
    return {
      currentSelection: [],
      objectivesUnchecked: [],
      isSubmitting: false,
      objectiveTooltipId: null,
      userLabStatusModified: null,
    }
  },
  props: {
    labStatus: {
      type: String,
      required: true,
    },
    labObjectivesPlain: {
      type: Array,
      required: true,
    },
    userLabStatus: {
      type: Object,
      required: false,
    },
    userLabAttempt: {
      type: Object,
      required: false,
    },
  },
  computed: {
    labRunning() {
      return this.labStatus === 'started'
    },
    objectivesCompleted() {
      return this.labObjectives.filter(labObj => labObj.has_passed)
    },
    allObjectivesCompleted() {
      return this.objectivesCompleted.length === this.labObjectives.length
    },
    attempts() {
      return this.userLabAttempt?.lab_objectives
    },
    labObjectives() {
      const mergedObjectives = this.labObjectivesPlain?.map(objective => {
        const userObjectives = this.userLabStatus?.lab_objectives.filter(
          user_objective => user_objective.lab_objective_id === objective.id
        )[0]
        const userLabAttempt = this.userLabAttempt?.lab_objectives.filter(
          user_objective => user_objective.lab_objective_id === objective.id
        )[0]
        const mergedObjective = {
          ...objective,
          ...userObjectives,
          userLabAttempt,
        }
        return mergedObjective
      })
      return mergedObjectives
    },
    objectivesStatusLabel() {
      if (this.isSubmitting)
        return `${this.currentSelection.length} of ${this.currentObjectivesAvailable} objectives submitting`
      if (!this.currentSelection.length) {
        return '0 objectives selected'
      }
      return `${this.currentSelection.length} of ${this.currentObjectivesAvailable} objectives selected`
    },
    currentObjectivesAvailable() {
      const notSubmittedObjectives = this.labObjectives.filter(
        objective =>
          objective.has_passed === null || objective.has_passed === false
      )
      return this.objectivesUnchecked.length + notSubmittedObjectives.length
    },
  },
  methods: {
    objectiveAttempt(objectiveId) {
      if (!this.attempts) return null
      return this.attempts.find(
        attempt => attempt.lab_objective_id === objectiveId
      )
    },
    lastSubmission(objective) {
      const objectiveAttempt = this.objectiveAttempt(objective.id)
      let status = objectiveAttempt || objective
      status = (({ has_passed, reason }) => ({ has_passed, reason }))(status)
      status.label = status.has_passed
        ? 'Last submission: Correct.'
        : 'Last submission: Incorrect.'
      status.show = status.has_passed != null
      return status
    },
    updateTooltip(objectiveId, enterEvent) {
      this.objectiveTooltipId = enterEvent ? objectiveId : null
    },
    resetObjective(objectiveId) {
      if (!this.labRunning || this.isSubmitting) return
      const uncheckedObjectives = this.objectivesUnchecked
      uncheckedObjectives.push(objectiveId)
      this.objectivesUnchecked = uncheckedObjectives.filter(
        (v, i, a) => a.indexOf(v) === i
      )
    },

    objectiveChecked(objectiveId) {
      const objectiveUnchecked = this.objectivesUnchecked.some(
        objective => objective === objectiveId
      )
      if (objectiveUnchecked) return false
      return this.objectiveHasPassed(objectiveId)
    },
    objectiveHasPassed(objectiveId) {
      const objective = this.getObjectiveById(objectiveId)
      return objective.userLabAttempt
        ? objective.userLabAttempt?.has_passed
        : objective?.has_passed
    },
    getObjectiveById(objectiveId) {
      return this.labObjectives.find(objective => objective.id === objectiveId)
    },
    showSubmissionLabel(objective) {
      if (objective.has_passed === null) return false
      const isReset = this.objectivesUnchecked.some(obj => obj === objective.id)
      if (objective.userLabAttempt) {
        const newAttempt =
          this.userLabStatusModified !== objective.userLabAttempt.modified
        return newAttempt && !isReset
          ? true
          : !objective.userLabAttempt.has_passed
      }
      return isReset ? !objective.has_passed : true
    },
    async submitPractice() {
      if (!this.currentSelection) return
      this.isSubmitting = true
      try {
        const response = await this.$store.dispatch(
          'labs/sendPTAPractice',
          this.currentSelection
        )
        this.userLabStatusModified = this.userLabStatus?.modified
        const objectivesAttemptsFailed = this.userLabAttempt?.lab_objectives.filter(
          user_objective => !user_objective.has_passed
        )
        const objectivesAttemptsFailedIds = objectivesAttemptsFailed.map(
          objective => objective.lab_objective_id
        )
        const objectivesAttemptsPassed = this.userLabAttempt?.lab_objectives.filter(
          user_objective => user_objective.has_passed
        )
        const objectivesAttemptsPassedIds = objectivesAttemptsPassed.map(
          objective => objective.lab_objective_id
        )
        this.objectivesUnchecked = this.objectivesUnchecked.filter(
          el => objectivesAttemptsPassedIds.indexOf(el) < 0
        )
        this.objectivesUnchecked = [
          ...new Set([
            ...objectivesAttemptsFailedIds,
            ...this.objectivesUnchecked,
          ]),
        ]
        this.currentSelection = []
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.objectives {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  margin: auto;
  &__container {
    width: 100%;
    padding: 23px 20px 20px 20px;
    background: var(--glass-dark);
    border-radius: 10px;
  }
  &__header {
    &-wrapper {
      display: flex;
      color: var(--white);
      font-size: 14px;
      font-weight: normal;
      align-items: center;
      width: 100%;
      justify-content: space-between;
    }
    &-title {
      font-size: 20px;
      font-weight: bold;
      margin-right: 10px;
    }
    &-completed {
      display: flex;
      align-items: center;
      &-label {
        margin-right: 10px;
      }
      &-badge {
        height: 32px;
        padding: 7px 14px;
        font-size: 12px;
        font-weight: 400;
        background: var(--glass-extra-light);
        border-radius: 20px;
        border: solid 1px var(--light-gray-darker);
        &--all {
          background: var(--positive-light);
          color: var(--charcoal);
          border-color: var(--positive-light);
        }
      }
    }
  }
  &__description {
    margin: 20px 0;
    text-align: left;
    line-height: 14px;
    color: var(--white);
    font-size: 12px;
    font-weight: normal;
    & p {
      margin: 0;
    }
  }
  &__list {
    display: flex;
    margin-bottom: 20px;
    padding-left: 0;
    font-size: 12px;
    flex-direction: column;
    &-item {
      display: flex;
      margin-bottom: 20px;
    }
    &-description {
      p {
        margin: 0 0 10px;
      }
      &-wrapper {
        padding-top: 1px;
        width: 100%;
        margin-left: 20px;
      }
      width: 100%;
      line-height: 14px;
      color: var(--light-blue-gray);
      text-align: left;
      flex-direction: column;
      display: flex;
      ::v-deep p {
        margin: 0 0 10px;
      }
    }
    &-submission {
      display: flex;
      position: relative;
      color: var(--white);
      &--passed {
        svg {
          width: 12.33px;
          height: 9.33px;
          path {
            stroke: var(--positive-light);
          }
        }
      }
      &--fail {
        svg {
          width: 9.33px;
          height: 9.33px;
        }
      }
      &-wrapper {
        margin-left: 7px;
        text-align: start;
        display: flex;
        align-items: center;
      }
      &-reason {
        display: inline;
        margin: 0;
        span {
          font-weight: 700;
        }
      }
      &-info {
        cursor: pointer;
        display: flex;
        svg {
          width: 14.67px;
          height: 14.67px;
          margin-left: 5.67px;
          stroke: var(--white);
          margin-bottom: 2px;
        }
      }
      &-reset {
        cursor: pointer;
        margin-left: 3px;
        display: inline;
        text-decoration: underline;
      }
      &-tooltip {
        position: absolute;
        display: flex;
        align-items: center;
        padding: 8px 16px;
        width: 200px;
        background: var(--light-gray-darker);
        border-radius: 4px;
        font-size: 14px;
        left: 226.67px;
        z-index: 100;
        &::before {
          content: '';
          transform: translateY(50%) rotate(135deg);
          position: absolute;
          width: 16px;
          height: 16px;
          left: -8px;
          bottom: 50%;
          background: var(--light-gray-darker);
        }
      }
    }
    &-btn {
      display: flex;
      position: relative;
      margin-left: 20px;
      padding: 7px 14px;
      background: var(--glass-extra-light);
      color: var(--white);
      border-radius: 20px;
      height: 32px;
      width: 106px;
      border: solid 1px var(--light-gray-darker);
      &--completed {
        padding: 7px 14px;
        background: var(--positive-light);
        color: var(--charcoal);
        border: 1px solid var(--positive-light);
        border-radius: 48px;
      }
    }
    &-checkbox {
      grid-gap: 0;
      &--disabled {
        cursor: default;
      }
      &__control {
        width: 24px;
        height: 24px;
        border: 1px solid var(--white);
        box-sizing: border-box;
        border-radius: 8px;
        svg {
          width: 9px;
          height: 6px;
        }
        &--disabled {
          border-color: var(--mid-gray-dark) !important;
          background: transparent !important;
          cursor: default;
          svg {
            path {
              stroke: var(--mid-gray-dark);
            }
          }
        }
        &--completed {
          background: var(--positive-light) !important;
          color: var(--charcoal) !important;
          border: 1px solid var(--positive-light) !important;
          border-radius: 48px;
          svg {
            transform: scale(1);
            path {
              stroke: var(--charcoal);
            }
          }
        }
      }
      &__input {
        input:checked
          + .answer-checkbox__control:not(.objectives__list-checkbox__control--completed),
        .answer-checkbox__input
          input.answer-checkbox--checked
          + .answer-checkbox__control:not(.objectives__list-checkbox__control--completed) {
          background: rgba(255, 255, 255, 0.1);
          border-color: var(--white);
        }
        .answer-checkbox__input
          input:checked
          + .answer-checkbox__control:not(.objectives__list-checkbox__control--completed) {
          border-color: var(--white);
        }
      }
    }
    &-tick-completed {
      background: var(--positive-light);
      color: var(--charcoal);
      border: 1px solid #70ffd4;
      border-radius: 48px;
    }
  }
  &__footer {
    margin-top: 20px;
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: space-between;
    &-status {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
    }
    &-submitting {
      display: flex;
      background: var(--chetwode-blue);
      box-shadow: 0px 0px 10px rgba(167, 147, 246, 0.5);
      border-radius: 30px;
      color: var(--charcoal);
      padding: 5px 10.5px;
      align-items: center;
      line-height: normal;
      ::v-deep.loading-small {
        margin-right: 5.17px;
        svg {
          width: 16px;
          path {
            stroke: var(--charcoal);
          }
        }
      }
    }
    &-checked {
      color: var(--white);
      font-size: 12px;
      line-height: 14px;
      font-weight: normal;
    }
    &-submit {
      margin-left: 20px;
    }
  }
}
</style>
